import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';

const AdminSupplies = () => {
    const [supplies, setSupplies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSupply, setEditingSupply] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        type: 'Box',
        quantity: 0,
        unit: 'Cái',
        costPrice: 0,
        minStock: 10,
        description: ''
    });

    const supplyTypes = [
        { value: 'Box', label: 'Box Carton' },
        { value: 'ShoeBox', label: 'Hộp giày' },
        { value: 'Sock', label: 'Vớ (Tất)' },
        { value: 'Spray', label: 'Chai khử mùi/Xịt nano' },
        { value: 'Other', label: 'Khác' }
    ];

    const fetchSupplies = async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin/supplies');
            setSupplies(response.data);
        } catch (error) {
            toast.error('Không thể tải dữ liệu kho hàng');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSupplies();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: name === 'quantity' || name === 'costPrice' || name === 'minStock' ? Number(value) : value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingSupply) {
                await api.put(`/admin/supplies/${editingSupply._id}`, formData);
                toast.success('Cập nhật vật tư thành công');
            } else {
                await api.post('/admin/supplies', formData);
                toast.success('Thêm vật tư mới thành công');
            }
            setIsModalOpen(false);
            setEditingSupply(null);
            setFormData({ name: '', type: 'Box', quantity: 0, unit: 'Cái', costPrice: 0, minStock: 10, description: '' });
            fetchSupplies();
        } catch (error) {
            toast.error('Lỗi khi lưu dữ liệu');
        }
    };

    const handleEdit = (supply) => {
        setEditingSupply(supply);
        setFormData({
            name: supply.name,
            type: supply.type,
            quantity: supply.quantity,
            unit: supply.unit,
            costPrice: supply.costPrice,
            minStock: supply.minStock,
            description: supply.description || ''
        });
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Bạn có chắc chắn muốn xóa vật tư này?')) return;
        try {
            await api.delete(`/admin/supplies/${id}`);
            toast.success('Đã xóa vật tư');
            fetchSupplies();
        } catch (error) {
            toast.error('Lỗi khi xóa');
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('vi-VN').format(amount) + '₫';
    };

    if (loading) return <div className="p-8 text-center font-bold">Đang tải...</div>;

    return (
        <div className="p-3 sm:p-8 bg-gray-50 min-h-full transition-all duration-300">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 sm:mb-8 gap-6">
                <div>
                    <h1 className="text-xl sm:text-3xl font-black text-slate-800 uppercase tracking-tight">Quản lý Kho phụ liệu</h1>
                    <p className="text-[10px] sm:text-sm text-slate-500 font-bold uppercase tracking-widest mt-1">Hộp, vớ, chai xịt và vật tư đóng gói</p>
                </div>
                <button 
                    onClick={() => { setEditingSupply(null); setIsModalOpen(true); }}
                    className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 sm:py-3 rounded-2xl font-black text-xs sm:text-sm transition-all shadow-xl shadow-blue-500/20 flex items-center justify-center gap-3 group uppercase tracking-widest"
                >
                    <span className="material-symbols-outlined group-hover:rotate-90 transition-transform text-[18px] sm:text-[24px]">add</span>
                    <span>NHẬP KHO MỚI</span>
                </button>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
                {supplyTypes.map(type => {
                    const items = supplies.filter(s => s.type === type.value);
                    const totalQty = items.reduce((sum, item) => sum + item.quantity, 0);
                    return (
                        <div key={type.value} className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-between">
                            <div>
                                <p className="text-[8px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{type.label}</p>
                                <p className="text-lg sm:text-2xl font-black text-slate-800">{totalQty}</p>
                            </div>
                            <p className="text-[8px] sm:text-[10px] font-bold text-slate-400 mt-2 uppercase tracking-tight">{items.length} loại</p>
                        </div>
                    );
                })}
            </div>

            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto scrollbar-hide">
                    <table className="w-full min-w-[600px] sm:min-w-full">
                        <thead className="bg-gray-50 border-b border-gray-100">
                            <tr>
                                <th className="px-4 sm:px-6 py-4 text-left text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest">Vật tư</th>
                                <th className="px-4 sm:px-6 py-4 text-left text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest">Loại</th>
                                <th className="px-4 sm:px-6 py-4 text-left text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest">Số lượng</th>
                                <th className="px-4 sm:px-6 py-4 text-left text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest">Giá nhập</th>
                                <th className="px-4 sm:px-6 py-4 text-left text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest">Trạng thái</th>
                                <th className="px-4 sm:px-6 py-4 text-right text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest">Thao tác</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {supplies.map(item => (
                                <tr key={item._id} className="hover:bg-blue-50/20 transition-colors group">
                                    <td className="px-4 sm:px-6 py-4 sm:py-5">
                                        <p className="text-xs sm:text-sm font-black text-slate-800 truncate max-w-[120px] sm:max-w-none">{item.name}</p>
                                        <p className="text-[9px] text-slate-400 font-bold uppercase mt-0.5 truncate max-w-[120px] sm:max-w-none">{item.description || 'Không có mô tả'}</p>
                                    </td>
                                    <td className="px-4 sm:px-6 py-4 sm:py-5">
                                        <span className="bg-slate-100 text-slate-600 px-2 py-0.5 sm:py-1 rounded-lg text-[9px] sm:text-[10px] font-black uppercase tracking-wider">
                                            {supplyTypes.find(t => t.value === item.type)?.label.split(' ')[0]}
                                        </span>
                                    </td>
                                    <td className="px-4 sm:px-6 py-4 sm:py-5 font-black text-slate-700 text-xs sm:text-sm">
                                        {item.quantity} <span className="text-[9px] text-gray-400">{item.unit}</span>
                                    </td>
                                    <td className="px-4 sm:px-6 py-4 sm:py-5 font-black text-blue-600 text-xs sm:text-sm">
                                        {formatCurrency(item.costPrice)}
                                    </td>
                                    <td className="px-4 sm:px-6 py-4 sm:py-5">
                                        {item.quantity <= item.minStock ? (
                                            <span className="bg-red-50 text-red-600 px-2 py-0.5 sm:py-1 rounded-lg text-[9px] sm:text-[10px] font-black uppercase tracking-wider border border-red-100">
                                                Hết
                                            </span>
                                        ) : (
                                            <span className="bg-green-50 text-green-600 px-2 py-0.5 sm:py-1 rounded-lg text-[9px] sm:text-[10px] font-black uppercase tracking-wider border border-green-100">
                                                Đủ
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 sm:px-6 py-4 sm:py-5 text-right">
                                        <div className="flex justify-end gap-1 sm:gap-2">
                                            <button onClick={() => handleEdit(item)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-xl transition-all">
                                                <span className="material-symbols-outlined text-[18px] sm:text-[20px]">edit</span>
                                            </button>
                                            <button onClick={() => handleDelete(item._id)} className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition-all">
                                                <span className="material-symbols-outlined text-[18px] sm:text-[20px]">delete</span>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fadeIn">
                    <div className="bg-white rounded-t-[32px] sm:rounded-[32px] w-full max-w-lg shadow-2xl overflow-hidden animate-slideUp max-h-[95vh] flex flex-col">
                        <div className="p-6 sm:p-8 border-b border-gray-100 flex items-center justify-between bg-slate-50">
                            <div>
                                <h2 className="text-lg sm:text-xl font-black text-slate-800 uppercase tracking-tight">
                                    {editingSupply ? 'Cập nhật' : 'Nhập kho mới'}
                                </h2>
                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Thông tin chi tiết phụ liệu</p>
                            </div>
                            <button onClick={() => setIsModalOpen(false)} className="bg-white text-slate-400 hover:text-red-500 p-2 rounded-2xl shadow-sm transition-colors">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-5 overflow-y-auto">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                                <div className="sm:col-span-2 space-y-1">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Tên vật tư</label>
                                    <input 
                                        type="text" name="name" value={formData.name} onChange={handleInputChange} required 
                                        className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-xs sm:text-sm"
                                        placeholder="Ví dụ: Box carton loại 1"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Loại</label>
                                    <select 
                                        name="type" value={formData.type} onChange={handleInputChange}
                                        className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-xs sm:text-sm"
                                    >
                                        {supplyTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Đơn vị (VD: Cái, Hộp...)</label>
                                    <input 
                                        type="text" name="unit" value={formData.unit} onChange={handleInputChange} required 
                                        className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-xs sm:text-sm"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Số lượng tồn</label>
                                    <input 
                                        type="number" name="quantity" value={formData.quantity} onChange={handleInputChange} required 
                                        className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-xs sm:text-sm"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Giá nhập mỗi đv</label>
                                    <input 
                                        type="number" name="costPrice" value={formData.costPrice} onChange={handleInputChange} required 
                                        className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-xs sm:text-sm"
                                    />
                                </div>
                                <div className="sm:col-span-2 space-y-1">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Mô tả (Tùy chọn)</label>
                                    <textarea 
                                        name="description" value={formData.description} onChange={handleInputChange}
                                        className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-xs sm:text-sm min-h-[80px] sm:min-h-[100px]"
                                    />
                                </div>
                            </div>
                            <button type="submit" className="w-full bg-slate-900 hover:bg-black text-white py-4 sm:py-5 rounded-[20px] font-black text-xs sm:text-sm uppercase tracking-widest transition-all shadow-xl">
                                {editingSupply ? 'Cập nhật thay đổi' : 'Xác nhận nhập kho'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminSupplies;
